import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  Typography,
  Link,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { Chart } from 'react-google-charts';
import { ICoupon } from '../../types/coupon';
import { ICouponStats } from '../../types/couponStats';

interface StatsDialogProps {
  open: boolean;
  onClose: () => void;
  coupon: ICoupon | null;
  stats: ICouponStats[];
}

type Order = 'asc' | 'desc';

const StatsDialog: React.FC<StatsDialogProps> = ({ open, onClose, coupon, stats }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<keyof ICouponStats>('dateCreated');
  const [order, setOrder] = useState<Order>('asc');

  const isCoupon = coupon?.notificationType === 'coupon';
  const pageTitle = isCoupon ? 'Coupon Statistic' : 'Notification Statistic';

  // Calculate totals
  const { totalAmount, totalQuantity } = useMemo(() => {
    let amount = 0;
    let quantity = 0;
    if (isCoupon) {
      stats.forEach((row) => {
        amount += row.totalAmount || 0;
        quantity += row.quantity || 0;
      });
    }
    return { totalAmount: amount, totalQuantity: quantity };
  }, [stats, isCoupon]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!coupon) return [['Label', 'Value']];
    
    const data: any[] = [['Label', 'Value']];

    data.push(['Acnts - ' + coupon.totalCount, (coupon.totalCount || 0) - (coupon.sentCount || 0)]);
    data.push(['Sent - ' + coupon.sentCount, (coupon.sentCount || 0) - (coupon.receivedCount || 0)]);
    data.push(['Delivered - ' + coupon.receivedCount, (coupon.receivedCount || 0) - (coupon.viewedCount || 0)]);
    data.push(['Viewed - ' + coupon.viewedCount, (coupon.viewedCount || 0) - (coupon.redeemedCount || 0)]);

    if (isCoupon) {
      data.push(['Redemptions - ' + coupon.redeemedCount, coupon.redeemedCount || 0]);
    }

    return data;
  }, [coupon, isCoupon]);

  const sortedStats = useMemo(() => {
    return [...stats].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return order === 'asc'
        ? (aValue < bValue ? -1 : 1)
        : (bValue < aValue ? -1 : 1);
    });
  }, [stats, order, orderBy]);

  const handleRequestSort = (property: keyof ICouponStats) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const paginatedStats = sortedStats.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const formatCouponValue = () => {
    if (!coupon) return '';
    const value = coupon.discountValue?.toFixed(2);
    return coupon.discountType === 'percentage' ? `${value}%` : `£${value}`;
  };

  const handleExport = () => {
    if (!coupon) return;
    
    const columns = isCoupon
      ? ['couponCode', 'customerNo', 'dateCreated', 'dateNotificationSent', 'notificationSentBy',
         'dateNotificationReceived', 'dateNotificationViewed', 'dateRedeemed', 'user', 'invoiceRef',
         'productCode', 'quantity', 'totalAmount', 'maximumRedemptions']
      : ['notificationText', 'customerNo', 'dateCreated', 'dateNotificationSent', 'notificationSentBy',
         'dateNotificationReceived', 'dateNotificationViewed'];

    // Create CSV content
    const headers = columns.join(',');
    const rows = stats.map(row =>
      columns.map(col => {
        const value = (row as any)[col];
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' && value.includes(',')) return `"${value}"`;
        return value;
      }).join(',')
    );

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${coupon.couponCode || 'stats'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (!coupon) return null;

  const displayedColumns = isCoupon
    ? [
        { id: 'couponCode', label: 'Coupon Code' },
        { id: 'customerNo', label: 'Customer No' },
        { id: 'dateCreated', label: 'Date/Time Created' },
        { id: 'dateNotificationSent', label: 'Date/Time Sent' },
        { id: 'notificationSentBy', label: 'Sent By' },
        { id: 'dateNotificationReceived', label: 'Date/Time Received' },
        { id: 'dateNotificationViewed', label: 'Date/Time Viewed' },
        { id: 'dateRedeemed', label: 'Date/Time Redeemed' },
        { id: 'user', label: 'User' },
        { id: 'invoiceRef', label: 'Invoice No' },
        { id: 'productCode', label: 'Product Code' },
        { id: 'quantity', label: 'Redeemed On Visit', align: 'right' },
        { id: 'totalAmount', label: 'Total Value', align: 'right' },
        { id: 'maximumRedemptions', label: 'Maximum Redemptions', align: 'right' },
      ]
    : [
        { id: 'notificationText', label: 'Notification Title' },
        { id: 'customerNo', label: 'Customer No' },
        { id: 'dateCreated', label: 'Date/Time Created' },
        { id: 'dateNotificationSent', label: 'Date/Time Sent' },
        { id: 'notificationSentBy', label: 'Sent By' },
        { id: 'dateNotificationReceived', label: 'Date/Time Received' },
        { id: 'dateNotificationViewed', label: 'Date/Time Viewed' },
      ];

  if (!coupon) {
    return null;
  }

  return (
    <Dialog 
      open={open && !!coupon} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          maxHeight: { xs: '95vh', sm: '90vh' },
          width: { xs: '100%', sm: 'auto' }
        }
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
        {pageTitle}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1, sm: 3 }, pb: { xs: 1, sm: 2 }, overflowY: 'auto' }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, v) => setTabValue(v)}
          sx={{ 
            minHeight: { xs: 40, sm: 48 },
            '& .MuiTab-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              minHeight: { xs: 40, sm: 48 },
              padding: { xs: '8px 12px', sm: '12px 16px' }
            }
          }}
        >
          <Tab label="Details" />
          <Tab label="Summary" />
        </Tabs>

        {tabValue === 0 && (
          <Box sx={{ mt: { xs: 1, sm: 2 } }}>
            {/* Mobile Card View */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {paginatedStats.map((row, index) => {
                const rowData: any = row;
                return (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {displayedColumns.map((column) => {
                          const value = rowData[column.id];
                          let displayValue = value || '';

                          if (column.id.includes('date') && value) {
                            displayValue = formatDate(value);
                          } else if ((column.id === 'quantity' || column.id === 'totalAmount') && value) {
                            displayValue = column.id === 'totalAmount'
                              ? Number(value).toFixed(2)
                              : value;
                          }

                          return (
                            <Box key={column.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: '40%' }}>
                                {column.label}:
                              </Typography>
                              <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word', maxWidth: '60%' }}>
                                {displayValue}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              {isCoupon && paginatedStats.length > 0 && (
                <Card sx={{ mt: 2, backgroundColor: 'action.hover' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight={600}>Total Quantity:</Typography>
                        <Typography variant="body2" fontWeight={600}>{totalQuantity}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight={600}>Total Amount:</Typography>
                        <Typography variant="body2" fontWeight={600}>{totalAmount.toFixed(2)}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={stats.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{ 
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            </Box>

            {/* Desktop Table View */}
            <TableContainer 
              component={Paper} 
              sx={{ 
                maxHeight: { xs: '60vh', sm: 500 }, 
                overflowX: 'auto',
                display: { xs: 'none', md: 'block' }
              }}
            >
              <Table stickyHeader sx={{ tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    {displayedColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={(column as any).align || 'left'}
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          padding: { xs: '8px', sm: '16px' },
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() => handleRequestSort(column.id as keyof ICouponStats)}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedStats.map((row, index) => (
                    <TableRow key={index}>
                      {displayedColumns.map((column) => {
                        const value = (row as any)[column.id];
                        let displayValue = value || '';

                        if (column.id.includes('date') && value) {
                          displayValue = formatDate(value);
                        } else if ((column.id === 'quantity' || column.id === 'totalAmount') && value) {
                          displayValue = column.id === 'totalAmount'
                            ? Number(value).toFixed(2)
                            : value;
                        }

                        return (
                          <TableCell
                            key={column.id}
                            align={(column as any).align || 'left'}
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              padding: { xs: '8px', sm: '16px' },
                              wordBreak: 'break-word'
                            }}
                          >
                            {displayValue}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
                {isCoupon && (
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={displayedColumns.length - 3}>
                        <strong>Total</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{totalQuantity}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{totalAmount.toFixed(2)}</strong>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                )}
              </Table>
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={stats.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </TableContainer>
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ mt: { xs: 1, sm: 2 }, display: 'flex', gap: { xs: 2, sm: 3 }, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box sx={{ flex: { xs: '1 1 auto', sm: 1 }, minWidth: { xs: '100%', sm: 400 }, width: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="body1" gutterBottom sx={{ fontSize: { xs: '0.95rem', sm: '1rem' }, fontWeight: 600 }}>
                {coupon.couponCode}
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                {coupon.notificationText}
              </Typography>
              {coupon.imageUrl && (
                <Box sx={{ my: { xs: 1, sm: 2 } }}>
                  <img 
                    src={coupon.imageUrl} 
                    alt="Coupon" 
                    style={{ 
                      width: '100%', 
                      maxWidth: '100%',
                      height: 'auto',
                      borderRadius: '8px'
                    }} 
                  />
                </Box>
              )}
              {coupon.notificationUrl && (
                <Typography variant="body2" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, wordBreak: 'break-all' }}>
                  <Link href={coupon.notificationUrl} target="_blank" rel="noopener">
                    Link
                  </Link>{' '}
                  {coupon.notificationUrl}
                </Typography>
              )}
              <Typography variant="body2" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {coupon.description}
              </Typography>
              {isCoupon && (
                <Box sx={{ mt: { xs: 1.5, sm: 2 }, display: 'flex', flexDirection: 'column', gap: { xs: 0.5, sm: 1 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <strong>Valid From:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      {formatDate(coupon.dateStart)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <strong>Valid To:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      {formatDate(coupon.dateEnd)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <strong>Coupon Type:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      {coupon.discountType}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <strong>Coupon Value:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      {formatCouponValue()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <strong>Maximum Redemptions:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      {coupon.maximumRedemptions}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <strong>Maximum No of Sends:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      {coupon.maxCount}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <strong>Products Codes:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, textAlign: 'right', wordBreak: 'break-word', maxWidth: '60%' }}>
                      {coupon.productCodes}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 300 }, mt: { xs: 2, sm: 0 } }}>
              <Chart
                chartType="PieChart"
                data={chartData}
                options={{ 
                  sliceVisibilityThreshold: 0,
                  chartArea: { left: 10, top: 10, width: '90%', height: '80%' },
                  legend: { 
                    position: isMobile ? 'bottom' : 'right',
                    textStyle: { fontSize: isMobile ? 10 : 12 }
                  }
                }}
                width="100%"
                height={isMobile ? "280px" : "400px"}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Button 
          onClick={onClose}
          fullWidth={isMobile}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Close
        </Button>
        <Button 
          onClick={handleExport} 
          variant="contained" 
          color="primary"
          fullWidth={isMobile}
          sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
        >
          Export
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatsDialog;
